import React from 'react';

export interface GuidedProgressBarProps {
  /** Total task count for the study. */
  total: number;
  /** Index of the currently-active task (0-based). Segments before it
      render as 'done'; the segment at this index renders as 'current'
      (shimmer); segments after render as 'upcoming'. When the study is
      complete, pass `total` (out of range) and all segments are done. */
  current: number;
  /** When true, the bar falls back to a "X of Y" textual label instead
      of segmented bar — used for studies with too many tasks for the
      bar to read cleanly (>12). */
  numericFallback?: boolean;
  /** Additional class names appended after `gd-progress`. */
  className?: string;
}

/** Switching point for numeric fallback. Above this the bar is too noisy. */
const NUMERIC_FALLBACK_THRESHOLD = 12;

/** Segmented progress bar used by every guided ceremony screen and by
    the header band overlaid on capture screens. */
export const GuidedProgressBar: React.FC<GuidedProgressBarProps> = ({
  total,
  current,
  numericFallback,
  className,
}) => {
  const useNumeric = numericFallback ?? total > NUMERIC_FALLBACK_THRESHOLD;
  const done = Math.min(current, total);

  if (useNumeric) {
    return (
      <div
        className={`gd-progress-label ${className ?? ''}`}
        role="progressbar"
        aria-valuenow={done}
        aria-valuemax={total}
        aria-label={`${done} of ${total} complete`}
      >
        Task {Math.min(done + 1, total)} of {total}
      </div>
    );
  }

  return (
    <div
      className={`gd-progress ${className ?? ''}`}
      role="progressbar"
      aria-valuenow={done}
      aria-valuemax={total}
      aria-label={`${done} of ${total} complete`}
    >
      {Array.from({ length: total }).map((_, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : '';
        return <div key={i} className={`gd-seg ${state ? `gd-seg--${state}` : ''}`} />;
      })}
    </div>
  );
};
